/*****************************************************
Naam: CustomerSettingsModal.jsx
Locatie: src/components/CustomerSettingsModal.jsx
Datum: 2026-01-20
Versie: 2.0.0
Auteur: Claude + Ragnar
******************************************************
BESCHRIJVING:
Settings modal voor klanten portal.
Toont de bedrijfsgegevens van de klant + account instellingen.
Klant kan bedrijfsgegevens wijzigen (ter goedkeuring).
Klant kan eigen wachtwoord wijzigen.

LAYOUT:
- 2 kolommen: Basis gegevens + Overige gegevens
- Account sectie: Username (read-only) + Wachtwoord wijzigen
- Bewerkbare input velden voor bedrijfsdata
- Draggable modal

FLOW BEDRIJFSGEGEVENS:
1. Klant wijzigt velden
2. Klant klikt "Wijzigingen indienen"
3. Wijzigingen gaan naar customer_pending_changes tabel
4. Dashboard krijgt melding
5. Admin keurt goed/af
6. Pas dan naar klanten.xlsx

CHANGELOG:
v2.0.0 (2026-01-20) - Ragnar + Claude
  - NIEUW: Bedrijfsgegevens bewerkbaar
  - NIEUW: Wijzigingen indienen ter goedkeuring
  - NIEUW: Pending changes status weergave
  - NIEUW: Edit mode toggle

v1.2.0 (2026-01-20) - Ragnar + Claude
  - Account sectie met username en wachtwoord

v1.1.0 (2026-01-20) - Ragnar + Claude
  - Restyled naar main dashboard stijl
******************************************************/

import { useLanguage } from '../../LanguageContext';
import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, EyeOff, Key, CheckCircle, AlertCircle, Edit3, Save } from 'lucide-react';

// Editable fields configuration - buiten component voor stabiele referentie
const EDITABLE_FIELDS = [
  { key: 'naam', label: 'Bedrijfsnaam', section: 'basis' },
  { key: 'adres', label: 'Adres', section: 'basis' },
  { key: 'postcode', label: 'Postcode', section: 'basis' },
  { key: 'plaats', label: 'Plaats', section: 'basis' },
  { key: 'land', label: 'Land', section: 'basis' },
  { key: 'website', label: 'Website', section: 'basis' },
  { key: 'contactpersoon', label: 'Contactpersoon', section: 'basis' },
  { key: 'telefoonContactpersoon', label: 'Telefoon', section: 'basis' },
  { key: 'emailContactpersoon', label: 'Email', section: 'basis' },
  { key: 'bank', label: 'Bank', section: 'overig' },
  { key: 'rekeningnr', label: 'Rekeningnummer', section: 'overig' },
  { key: 'btwVatNr', label: 'BTW/VAT nr', section: 'overig' },
  { key: 'kvkNr', label: 'KvK nr', section: 'overig' },
  { key: 'factuurNaam', label: 'Factuur naam', section: 'factuur' },
  { key: 'factuurAdres', label: 'Factuur adres', section: 'factuur' },
  { key: 'factuurPostcode', label: 'Factuur postcode', section: 'factuur' },
  { key: 'factuurPlaats', label: 'Factuur plaats', section: 'factuur' }
];

const CustomerSettingsModal = ({
  show, onClose, customerId }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [originalData, setOriginalData] = useState(null); // Original data for comparison
  const [accountData, setAccountData] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoSaving, setLogoSaving] = useState(false);
  const logoInputRef = React.useRef(null);
  
  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  
  // Password change state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  useEffect(() => {
    if (show) {
      loadCustomerData();
      loadAccountData();
      setPosition({ x: 0, y: 0 });
      setIsEditMode(false);
      setHasChanges(false);
      setSubmitSuccess('');
      setSubmitError('');
      // Reset password form
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordError('');
      setPasswordSuccess('');
    }
  }, [show, customerId]);

  // Check for changes when customerData updates
  useEffect(() => {
    if (customerData && originalData) {
      const changed = EDITABLE_FIELDS.some(field => 
        (customerData[field.key] || '') !== (originalData[field.key] || '')
      );
      setHasChanges(changed);
    }
  }, [customerData, originalData]);

  // Draggable handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.modal-header')) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left - position.x,
        y: e.clientY - rect.top - position.y
      });
    }
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        if (modalRef.current) {
          const rect = modalRef.current.getBoundingClientRect();
          const newX = e.clientX - dragOffset.x - (window.innerWidth - rect.width) / 2;
          const newY = e.clientY - dragOffset.y - (window.innerHeight - rect.height) / 2;
          setPosition({ x: newX, y: newY });
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const loadCustomerData = async () => {
    try {
      const lr = await fetch('/api/customer/logo', { credentials: 'include' });
      if (lr.ok) { const ld = await lr.json(); setLogo(ld.logo || null); }
    } catch(e) {}
    setLoading(true);
    try {
      const response = await fetch('/api/customer/settings', {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setCustomerData(data.customer);
        setOriginalData({ ...data.customer }); // Store original for comparison
      }
    } catch (err) {
      console.error('Load customer settings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountData = async () => {
    try {
      const response = await fetch('/api/customer/account', {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setAccountData(data.account);
      }
    } catch (err) {
      console.error('Load account data error:', err);
    }
  };

  const handleFieldChange = (key, value) => {
    setCustomerData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmitChanges = async () => {
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    // Collect all changed fields
    const changes = EDITABLE_FIELDS
      .filter(field => (customerData[field.key] || '') !== (originalData[field.key] || ''))
      .map(field => ({
        field: field.key,
        oldValue: originalData[field.key] || '',
        newValue: customerData[field.key] || ''
      }));

    if (changes.length === 0) {
      setSubmitError('Geen wijzigingen gevonden');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/customer/submit-settings-change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ changes })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitSuccess(data.message || 'Gegevens succesvol bijgewerkt!');
        setIsEditMode(false);
        setOriginalData({ ...customerData }); // Update original
        setHasChanges(false);
        // Reload data to confirm changes
        loadCustomerData();
      } else {
        setSubmitError(typeof data.error === 'object' ? (data.error.message || 'Er is iets misgegaan') : (data.error || 'Er is iets misgegaan'));
      }
    } catch (err) {
      console.error('Submit changes error:', err);
      setSubmitError('Netwerkfout. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setCustomerData({ ...originalData }); // Restore original
    setIsEditMode(false);
    setHasChanges(false);
    setSubmitError('');
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword) {
      setPasswordError('Voer uw huidige wachtwoord in');
      return;
    }

    if (!passwordForm.newPassword || passwordForm.newPassword.length < 8) {
      setPasswordError('Nieuw wachtwoord moet minimaal 8 karakters zijn');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Nieuwe wachtwoorden komen niet overeen');
      return;
    }

    setChangingPassword(true);

    try {
      const response = await fetch('/api/customer/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setPasswordSuccess('Wachtwoord succesvol gewijzigd!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPasswordError(typeof data.error === 'object' ? (data.error.message || 'Er is iets misgegaan') : (data.error || 'Er is iets misgegaan'));
      }
    } catch (err) {
      console.error('Change password error:', err);
      setPasswordError('Netwerkfout. Probeer het opnieuw.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2000000) { alert('Logo mag maximaal 2MB zijn.'); return; }
    setLogoSaving(true);
    try {
      // Toon preview direct
      const reader = new FileReader();
      reader.onload = (ev) => setLogo(ev.target.result);
      reader.readAsDataURL(file);
      // Upload als bestand (FormData)
      const formData = new FormData();
      formData.append('logo', file);
      const res = await fetch('/api/customer/logo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) alert('Logo opslaan mislukt.');
    } catch(err) {
      alert('Verbindingsfout bij opslaan logo.');
    } finally { setLogoSaving(false); }
  };
  const handleLogoRemove = async () => {
    setLogo(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
    try { await fetch('/api/customer/logo', { method: 'DELETE', credentials: 'include' }); } catch(e) {}
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div 
        ref={modalRef}
        className="bg-gray-100 rounded-none shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseDown={handleMouseDown}
      >
        
        {/* Header */}
        <div className="modal-header bg-gray-200 px-6 py-4 flex justify-between items-center cursor-grab active:cursor-grabbing select-none border-b border-gray-300">
          <h2 className="text-xl font-semibold text-gray-800">{t('instellingen')}</h2>
          <div className="flex items-center gap-2">
            {!isEditMode && !loading && customerData && (
              <button
                onClick={() => setIsEditMode(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-none hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Edit3 className="w-4 h-4" />
                Bewerken
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1 rounded transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
            </div>
          ) : !customerData ? (
            <div className="text-center py-12 text-gray-500">
              Geen gegevens gevonden
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Edit mode notification */}
              {isEditMode && (
                <div className="bg-blue-50 border border-blue-200 rounded-none p-4">
                  <div className="flex items-start gap-3">
                    <Edit3 className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-blue-800 font-medium">Bewerkmodus actief</p>
                      <p className="text-blue-600 text-sm">
                        Wijzig uw gegevens en klik op "Opslaan". 
                        Wijzigingen worden direct doorgevoerd.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success/Error messages */}
              {submitSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-none p-4 flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  {submitSuccess}
                </div>
              )}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-none p-4 flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  {submitError}
                </div>
              )}

              {/* Bedrijfsgegevens */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Linker kolom: Basis gegevens */}
                <div className="bg-amber-50 rounded-none p-5 border border-amber-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('basis_gegevens')}</h3>
                  
                  {/* Naam */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Naam
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.naam || ''}
                        onChange={(e) => handleFieldChange('naam', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.naam || '') !== (originalData.naam || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.naam || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Adres */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Adres
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.adres || ''}
                        onChange={(e) => handleFieldChange('adres', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.adres || '') !== (originalData.adres || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.adres || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Postcode */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Postcode
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.postcode || ''}
                        onChange={(e) => handleFieldChange('postcode', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.postcode || '') !== (originalData.postcode || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.postcode || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Plaats */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Plaats
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.plaats || ''}
                        onChange={(e) => handleFieldChange('plaats', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.plaats || '') !== (originalData.plaats || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.plaats || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Land */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Land
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.land || ''}
                        onChange={(e) => handleFieldChange('land', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.land || '') !== (originalData.land || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.land || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Website */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Website
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.website || ''}
                        onChange={(e) => handleFieldChange('website', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.website || '') !== (originalData.website || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.website || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>
                  
                  <div className="border-t border-amber-300 my-4"></div>
                  
                  {/* Contactpersoon */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Contactpersoon
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.contactpersoon || ''}
                        onChange={(e) => handleFieldChange('contactpersoon', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.contactpersoon || '') !== (originalData.contactpersoon || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.contactpersoon || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Telefoon */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Telefoon
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.telefoonContactpersoon || ''}
                        onChange={(e) => handleFieldChange('telefoonContactpersoon', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.telefoonContactpersoon || '') !== (originalData.telefoonContactpersoon || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.telefoonContactpersoon || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Email */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Email
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.emailContactpersoon || ''}
                        onChange={(e) => handleFieldChange('emailContactpersoon', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.emailContactpersoon || '') !== (originalData.emailContactpersoon || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.emailContactpersoon || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>
                </div>

                {/* Rechter kolom: Overige gegevens */}
                <div className="bg-amber-50 rounded-none p-5 border border-amber-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('overige_gegevens')}</h3>
                  
                  {/* Bank */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Bank
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.bank || ''}
                        onChange={(e) => handleFieldChange('bank', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.bank || '') !== (originalData.bank || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.bank || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Rekeningnr */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Rekeningnr
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.rekeningnr || ''}
                        onChange={(e) => handleFieldChange('rekeningnr', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.rekeningnr || '') !== (originalData.rekeningnr || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.rekeningnr || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* BTW/VAT nr */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      BTW/VAT nr
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.btwVatNr || ''}
                        onChange={(e) => handleFieldChange('btwVatNr', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.btwVatNr || '') !== (originalData.btwVatNr || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.btwVatNr || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* BTW tarief (read-only) */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">BTW tarief</label>
                    <input
                      type="text"
                      value={customerData.btwTarief || '-'}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-100 border border-gray-200 rounded-none text-gray-500 text-sm cursor-not-allowed"
                      title="BTW tarief kan niet gewijzigd worden"
                    />
                  </div>

                  {/* KvK nr */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      KvK nr
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.kvkNr || ''}
                        onChange={(e) => handleFieldChange('kvkNr', e.target.value)}
                        
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.kvkNr || '') !== (originalData.kvkNr || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.kvkNr || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>
                  
                  <div className="border-t border-amber-300 my-4"></div>
                  
                  <h4 className="text-md font-semibold text-gray-700 mb-3">{t('factuuradres')}</h4>

                  {/* Factuur Naam */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Naam
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.factuurNaam || ''}
                        onChange={(e) => handleFieldChange('factuurNaam', e.target.value)}
                        
                        placeholder={customerData.naam || ''}
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.factuurNaam || '') !== (originalData.factuurNaam || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.factuurNaam || customerData.naam || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Factuur Adres */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Adres
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.factuurAdres || ''}
                        onChange={(e) => handleFieldChange('factuurAdres', e.target.value)}
                        
                        placeholder={customerData.adres || ''}
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.factuurAdres || '') !== (originalData.factuurAdres || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.factuurAdres || customerData.adres || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Factuur Postcode */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Postcode
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.factuurPostcode || ''}
                        onChange={(e) => handleFieldChange('factuurPostcode', e.target.value)}
                        
                        placeholder={customerData.postcode || ''}
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.factuurPostcode || '') !== (originalData.factuurPostcode || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.factuurPostcode || customerData.postcode || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>

                  {/* Factuur Plaats */}
                  <div className="flex items-center mb-3">
                    <label className="w-32 text-sm font-medium text-gray-600 flex-shrink-0">
                      Plaats
                      
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={customerData.factuurPlaats || ''}
                        onChange={(e) => handleFieldChange('factuurPlaats', e.target.value)}
                        
                        placeholder={customerData.plaats || ''}
                        className={`flex-1 px-3 py-2 border rounded-none text-gray-700 text-sm ${
                          originalData && (customerData.factuurPlaats || '') !== (originalData.factuurPlaats || '')
                            ? 'bg-yellow-50 border-yellow-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <input type="text" value={customerData.factuurPlaats || customerData.plaats || '-'} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default" />
                    )}
                  </div>
                </div>
              </div>

              {/* Edit mode action buttons */}
              {isEditMode && (
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-none hover:bg-gray-400 transition-colors font-medium"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleSubmitChanges}
                    disabled={!hasChanges || submitting}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-none hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Bezig...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Opslaan
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Logo upload sectie */}
              <div className="bg-amber-50 rounded-none p-5 border border-amber-200 mb-0">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <span style={{fontSize:18}}>🖼</span>
                  Bedrijfslogo voor documenten
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Dit logo verschijnt op alle afgedrukte documenten en rapporten. Aanbevolen: PNG of SVG, max. 500KB.
                </p>
                <div style={{display:'flex', alignItems:'flex-start', gap:16}}>
                  {/* Logo preview */}
                  <div style={{
                    width:120, height:80, border:'2px dashed #d4a848', borderRadius:8,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    background:'white', flexShrink:0, overflow:'hidden',
                  }}>
                    {logo
                      ? <img src={logo} alt="Logo" style={{maxWidth:'100%', maxHeight:'100%', objectFit:'contain'}}/>
                      : <span style={{fontSize:28, color:'#d4a848'}}>🏢</span>}
                  </div>
                  {/* Upload controls */}
                  <div style={{flex:1}}>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{display:'none'}}
                    />
                    <div style={{display:'flex', gap:8, marginBottom:8}}>
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 transition-colors"
                      >
                        {logo ? t('ander_logo') : t('logo_uploaden')}
                      </button>
                      {logo && (
                        <button
                          onClick={handleLogoRemove}
                          className="px-4 py-2 bg-red-100 text-red-700 text-sm font-medium rounded hover:bg-red-200 transition-colors"
                        >
                          Verwijderen
                        </button>
                      )}
                    </div>
                    {logo
                      ? <p className="text-xs text-green-600 font-medium">✓ Logo opgeslagen — zichtbaar op alle documenten</p>
                      : <p className="text-xs text-gray-400">Nog geen logo geüpload</p>}
                  </div>
                </div>
              </div>

              {/* Account sectie - ONDERAAN */}
              <div className="bg-blue-50 rounded-none p-5 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-blue-600" />
                  Account
                </h3>
                
                {/* Username (read-only) */}
                <div className="flex items-center mb-4">
                  <label className="w-40 text-sm font-medium text-gray-600 flex-shrink-0">
                    Gebruikersnaam
                  </label>
                  <input
                    type="text"
                    value={accountData?.username || '-'}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-none text-gray-700 text-sm cursor-default"
                  />
                </div>

                {/* Password change section */}
                <div className="border-t border-blue-200 pt-4 mt-4">
                  <h4 className="text-md font-medium text-gray-700 mb-3">{t('wachtwoord_wijzigen')}</h4>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Current password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Huidig wachtwoord
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-none text-gray-700 text-sm pr-10"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* New password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Nieuw wachtwoord
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-none text-gray-700 text-sm pr-10"
                          placeholder="Min. 8 karakters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Bevestig wachtwoord
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-none text-gray-700 text-sm"
                        placeholder="Herhaal wachtwoord"
                      />
                    </div>
                  </div>

                  {/* Error/Success messages */}
                  {passwordError && (
                    <div className="flex items-center gap-2 text-red-600 text-sm mt-3">
                      <AlertCircle className="w-4 h-4" />
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div className="flex items-center gap-2 text-green-600 text-sm mt-3">
                      <CheckCircle className="w-4 h-4" />
                      {passwordSuccess}
                    </div>
                  )}

                  {/* Change password button */}
                  <button
                    onClick={handlePasswordChange}
                    disabled={changingPassword}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-none hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {changingPassword ? 'Bezig...' : 'Wachtwoord wijzigen'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-200 border-t border-gray-300 px-6 py-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {isEditMode 
              ? 'Wijzigingen worden direct opgeslagen.' 
              : 'Klik op "Bewerken" om uw gegevens aan te passen.'}
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-none hover:bg-gray-400 transition-colors font-medium"
          >
            {'\u2190'} Terug
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSettingsModal;
